import type { ChangeEvent, Dispatch, RefObject, SetStateAction } from 'react'
import { makeToastWithLoading, closeToaster } from 'shared/components/Toaster'
import type { TFunction } from 'shared/translations'

export const MAX_FILE_SIZE = 1024 * 1024 * 5 // 5MB
export const ACCEPTED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml']
export const ACCEPTED_FILE_TYPES_STRING = '.jpg,.jpeg,.png,.svg'

export const validateFile = (file: File, t: TFunction): string | null => {
  if (file.size > MAX_FILE_SIZE) {
    return t('avatarUpload.fileSizeTooLarge')
  }

  if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
    return t('avatarUpload.fileTypeNotAllowed')
  }

  return null
}

export const clearFileInput = (fileInputRef: RefObject<HTMLInputElement | null>): void => {
  if (fileInputRef.current !== null) {
    fileInputRef.current.value = ''
  }
}

export const handleValidationError = (params: {
  error: string
  isEdit?: boolean
  t: TFunction
  setUploadErrorMessage?: Dispatch<SetStateAction<string | null>>
  fileInputRef: RefObject<HTMLInputElement | null>
}): void => {
  const { error, isEdit, t, setUploadErrorMessage, fileInputRef } = params

  const { toastOnError } = makeToastWithLoading({
    headerMessage: t('toasts.uploadingImage')
  })

  setUploadErrorMessage?.(error)
  closeToaster()

  if (isEdit != null && !isEdit) {
    toastOnError(error)
  }

  clearFileInput(fileInputRef)
}

export const handleFileChange = (params: {
  event: ChangeEvent<HTMLInputElement>
  isEdit?: boolean
  t: TFunction
  uploadAvatar: (file: File) => Promise<void>
  onUploadSuccess?: () => void
  setIsAvatarSet?: Dispatch<SetStateAction<boolean>>
  setUploadErrorMessage?: Dispatch<SetStateAction<string | null>>
  fileInputRef: RefObject<HTMLInputElement | null>
}): void => {
  const { event, isEdit, t, uploadAvatar, onUploadSuccess, setIsAvatarSet, setUploadErrorMessage, fileInputRef } =
    params

  setUploadErrorMessage?.(null)

  const files = event.target.files
  if (files === null || files.length === 0) return

  const file = files[0]
  const validationError = validateFile(file, t)

  if (validationError !== null) {
    handleValidationError({
      error: validationError,
      isEdit,
      t,
      setUploadErrorMessage,
      fileInputRef
    })
    return
  }

  const { toastOnSuccess, toastOnError } = makeToastWithLoading({
    headerMessage: t('toasts.uploadingImage')
  })

  uploadAvatar(file)
    .then(() => {
      setIsAvatarSet?.(true)
      onUploadSuccess?.()
      toastOnSuccess(t('toasts.imageUploadSuccess'))
    })
    .catch(() => {
      toastOnError(t('toasts.imageUploadFailed'))
    })
    .finally(() => {
      clearFileInput(fileInputRef)
    })
}

export const handleAvatarUploadClick = (params: {
  fileInputRef: RefObject<HTMLInputElement | null>
  setUploadErrorMessage?: Dispatch<SetStateAction<string | null>>
}): void => {
  const { fileInputRef, setUploadErrorMessage } = params
  setUploadErrorMessage?.(null)
  if (fileInputRef.current !== null) {
    fileInputRef.current.click()
  }
}
